/**
 * The maths a stick needs, shared by the screen pad and the gamepad and kept out of both so it can
 * be tested without a browser or a controller plugged in.
 */

/** How far off centre a stick must be pushed before it means anything: below this it is a wobble. */
export const STICK_DEAD_ZONE = 0.15;
/** How hard a stick must be pushed before it also points the fighter rather than only moving it. */
export const AIM_THRESHOLD = 0.35;

/**
 * A stick's reading with its dead zone taken out, never longer than 1.
 *
 * The dead zone is a circle rather than a cross: taken per axis, a gentle diagonal loses whichever
 * axis is the smaller and the stick snaps to the horizontal. Past the edge of it the reading rises
 * from zero rather than jumping straight to the threshold, so a fighter creeps before it runs.
 */
export function applyDeadZone(
  x: number,
  z: number,
  dead = STICK_DEAD_ZONE,
): { x: number; z: number } {
  const length = Math.hypot(x, z);
  if (length <= dead) return { x: 0, z: 0 };
  const scale = Math.min(1, (length - dead) / (1 - dead)) / length;
  return { x: x * scale, z: z * scale };
}

/** Whether a stick is pushed hard enough to be an aim and not just a drift. */
export function aims(x: number, z: number): boolean {
  return Math.hypot(x, z) > AIM_THRESHOLD;
}

/**
 * A thumb's reading on a screen stick: where the stick was planted, where the thumb is now, and
 * how far from the centre counts as pushed all the way.
 *
 * Beyond that radius it saturates instead of running away with the thumb, which is what lets a
 * player shove into a corner and hold it there. Screen y grows downwards and the arena's z grows
 * up the screen, so the two are opposite.
 */
export function screenStick(
  base: { x: number; y: number },
  at: { x: number; y: number },
  radius: number,
): { x: number; z: number } {
  return applyDeadZone((at.x - base.x) / radius, -(at.y - base.y) / radius);
}
